import { useEffect, useState, type FormEvent } from 'react'
import { api, ApiFailure, type Doctor, type Letterhead } from '../api'
import CalibrationSheet from '../components/CalibrationSheet'

const BLANK = {
  fullName: '', qualifications: '', registrationNo: '', specialization: '', gender: '' as '' | 'male' | 'female' | 'other',
  phone: '', email: '', photoUrl: '', bio: '',
  consultationFee: '500', followUpFee: '0',
  followUpWindowDays: 7, displayOrder: 0, isActive: true,
}

export default function Doctors() {
  const [doctors, setDoctors] = useState<Doctor[]>([])
  const [selected, setSelected] = useState<Doctor | null>(null)
  const [letterhead, setLetterhead] = useState<Letterhead | null>(null)
  const [form, setForm] = useState({ ...BLANK })
  const [creating, setCreating] = useState(false)
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null)
  const [busy, setBusy] = useState(false)
  const [calibrating, setCalibrating] = useState(false)

  async function load() {
    const res = await api.get<{ doctors: Doctor[] }>('/api/doctors?includeInactive=true')
    setDoctors(res.doctors)
  }

  useEffect(() => { void load() }, [])

  async function openDoctor(id: string) {
    setMsg(null)
    setCreating(false)
    const res = await api.get<{ doctor: Doctor; letterhead: Letterhead | null }>(`/api/doctors/${id}`)
    setSelected(res.doctor)
    setLetterhead(res.letterhead)
    setForm({
      fullName: res.doctor.fullName,
      qualifications: res.doctor.qualifications ?? '',
      registrationNo: res.doctor.registrationNo ?? '',
      specialization: res.doctor.specialization ?? '',
      gender: res.doctor.gender ?? '',
      phone: res.doctor.phone ?? '',
      email: res.doctor.email ?? '',
      photoUrl: res.doctor.photoUrl ?? '',
      bio: res.doctor.bio ?? '',
      consultationFee: res.doctor.consultationFee,
      followUpFee: res.doctor.followUpFee,
      followUpWindowDays: res.doctor.followUpWindowDays,
      displayOrder: res.doctor.displayOrder,
      isActive: res.doctor.isActive,
    })
  }

  function startNew() {
    setCreating(true)
    setSelected(null)
    setLetterhead(null)
    setForm({ ...BLANK })
    setMsg(null)
  }

  async function saveDoctor(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setMsg(null)
    try {
      if (creating) {
        const res = await api.post<{ doctor: Doctor }>('/api/doctors', form)
        await load()
        await openDoctor(res.doctor.id)
        setMsg({ kind: 'ok', text: 'Doctor added.' })
      } else if (selected) {
        await api.patch(`/api/doctors/${selected.id}`, form)
        await load()
        setMsg({ kind: 'ok', text: 'Saved.' })
      }
    } catch (err) {
      setMsg({ kind: 'error', text: err instanceof ApiFailure ? err.message : 'Could not save.' })
    } finally {
      setBusy(false)
    }
  }

  async function saveLetterhead(e: FormEvent) {
    e.preventDefault()
    if (!selected || !letterhead) return
    setBusy(true)
    setMsg(null)
    try {
      const res = await api.put<{ letterhead: Letterhead }>(
        `/api/doctors/${selected.id}/letterhead`,
        {
          mode: letterhead.mode,
          marginTopMm: letterhead.marginTopMm,
          marginBottomMm: letterhead.marginBottomMm,
          marginLeftMm: letterhead.marginLeftMm,
          marginRightMm: letterhead.marginRightMm,
          showVitalsStrip: letterhead.showVitalsStrip,
          showAllergyBanner: letterhead.showAllergyBanner,
          headerNote: letterhead.headerNote,
          footerNote: letterhead.footerNote,
        },
      )
      setLetterhead(res.letterhead)
      setMsg({ kind: 'ok', text: 'Letterhead settings saved.' })
    } catch (err) {
      setMsg({ kind: 'error', text: err instanceof ApiFailure ? err.message : 'Could not save.' })
    } finally {
      setBusy(false)
    }
  }

  function setMargin(key: keyof Letterhead, value: string) {
    setLetterhead((lh) => (lh ? { ...lh, [key]: value } : lh))
  }

  return (
    <>
      <div className="page-head">
        <h2>Doctors</h2>
        <p>
          Each doctor carries their own fee, follow-up window and letterhead geometry. The
          registration number is printed in the prescription header and is legally required.
        </p>
      </div>

      {msg && <div className={`msg ${msg.kind}`}>{msg.text}</div>}

      <div className="panel">
        <h3>Roster</h3>
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Name</th><th>Specialisation</th><th>Fee</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {doctors.map((d) => (
                <tr key={d.id}>
                  <td>{d.fullName}</td>
                  <td>{d.specialization ?? '—'}</td>
                  <td className="num">₹{d.consultationFee}</td>
                  <td><span className={`pill ${d.isActive ? 'on' : 'off'}`}>{d.isActive ? 'active' : 'inactive'}</span></td>
                  <td><button className="link" onClick={() => void openDoctor(d.id)}>Edit</button></td>
                </tr>
              ))}
              {doctors.length === 0 && (
                <tr><td colSpan={5}><div className="empty">No doctors yet.</div></td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="actions">
          <button className="secondary" onClick={startNew}>Add a doctor</button>
        </div>
      </div>

      {(creating || selected) && (
        <form className="panel" onSubmit={(e) => void saveDoctor(e)}>
          <h3>{creating ? 'New doctor' : form.fullName}</h3>
          <div className="grid two">
            <div className="field">
              <label htmlFor="fullName">Full name</label>
              <input id="fullName" value={form.fullName}
                onChange={(e) => setForm({ ...form, fullName: e.target.value })} required />
            </div>
            <div className="field">
              <label htmlFor="qual">Qualifications</label>
              <input id="qual" value={form.qualifications} placeholder="MBBS, MD (General Medicine)"
                onChange={(e) => setForm({ ...form, qualifications: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="reg">Medical council registration no.</label>
              <input id="reg" value={form.registrationNo}
                onChange={(e) => setForm({ ...form, registrationNo: e.target.value })} />
              <span className="hint">Printed in the letterhead header.</span>
            </div>
            <div className="field">
              <label htmlFor="spec">Specialisation</label>
              <input id="spec" value={form.specialization}
                onChange={(e) => setForm({ ...form, specialization: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="dgender">Gender</label>
              <select id="dgender" value={form.gender}
                onChange={(e) => setForm({ ...form, gender: e.target.value as typeof form.gender })}>
                <option value="">Not set</option>
                <option value="female">Female</option>
                <option value="male">Male</option>
                <option value="other">Other</option>
              </select>
              <span className="hint">Picks the drawn picture shown when there is no photo.</span>
            </div>
            <div className="field">
              <label htmlFor="phone">Phone</label>
              <input id="phone" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="email">Email</label>
              <input id="email" type="email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="photo">Photo URL</label>
              <input id="photo" type="url" value={form.photoUrl} placeholder="https://…/dr-rao.jpg"
                onChange={(e) => setForm({ ...form, photoUrl: e.target.value })} />
              <span className="hint">Shown on the doctor&rsquo;s profile in the mobile app. Left empty, their initials stand in.</span>
            </div>
            <div className="field">
              <label htmlFor="bio">About</label>
              <textarea id="bio" rows={3} value={form.bio}
                placeholder="A few sentences a patient reads before choosing this doctor."
                onChange={(e) => setForm({ ...form, bio: e.target.value })} />
            </div>
          </div>

          <div className="grid three" style={{ marginTop: 14 }}>
            <div className="field">
              <label htmlFor="fee">Consultation fee</label>
              <input id="fee" type="number" step="1" value={form.consultationFee}
                onChange={(e) => setForm({ ...form, consultationFee: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="fufee">Follow-up fee</label>
              <input id="fufee" type="number" step="1" value={form.followUpFee}
                onChange={(e) => setForm({ ...form, followUpFee: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="window">Follow-up window (days)</label>
              <input id="window" type="number" value={form.followUpWindowDays}
                onChange={(e) => setForm({ ...form, followUpWindowDays: Number(e.target.value) })} />
              <span className="hint">A repeat visit inside this many days bills at the follow-up fee.</span>
            </div>
          </div>

          <div className="actions">
            <label className="check">
              <input type="checkbox" checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
              Active on the roster
            </label>
          </div>

          <div className="actions">
            <button type="submit" disabled={busy}>{creating ? 'Add doctor' : 'Save changes'}</button>
            <button type="button" className="secondary"
              onClick={() => { setSelected(null); setCreating(false); setLetterhead(null) }}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {selected && letterhead && (
        <form className="panel" onSubmit={(e) => void saveLetterhead(e)}>
          <h3>Letterhead &mdash; {selected.fullName}</h3>
          <p className="panel-note">
            Set <strong>pre-printed</strong> if the clinic already owns printed letterhead paper; only the
            body will be rendered, into the blank area. Margins are in millimetres on a 210 &times; 297 mm sheet.
          </p>

          <div className="grid two">
            <div className="field">
              <label htmlFor="mode">Print mode</label>
              <select id="mode" value={letterhead.mode}
                onChange={(e) => setLetterhead({ ...letterhead, mode: e.target.value as Letterhead['mode'] })}>
                <option value="full">Full print on blank A4</option>
                <option value="preprinted">Pre-printed stationery (body only)</option>
              </select>
            </div>
            <div className="field">
              <label htmlFor="footer">Footer note</label>
              <input id="footer" value={letterhead.footerNote ?? ''}
                onChange={(e) => setLetterhead({ ...letterhead, footerNote: e.target.value })} />
            </div>
          </div>

          <div className="grid three" style={{ marginTop: 14 }}>
            <div className="field">
              <label htmlFor="mt">Top margin (mm)</label>
              <input id="mt" type="number" step="0.5" value={letterhead.marginTopMm}
                onChange={(e) => setMargin('marginTopMm', e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="mb">Bottom margin (mm)</label>
              <input id="mb" type="number" step="0.5" value={letterhead.marginBottomMm}
                onChange={(e) => setMargin('marginBottomMm', e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="ml">Left margin (mm)</label>
              <input id="ml" type="number" step="0.5" value={letterhead.marginLeftMm}
                onChange={(e) => setMargin('marginLeftMm', e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="mr">Right margin (mm)</label>
              <input id="mr" type="number" step="0.5" value={letterhead.marginRightMm}
                onChange={(e) => setMargin('marginRightMm', e.target.value)} />
            </div>
          </div>

          <div className="actions">
            <label className="check">
              <input type="checkbox" checked={letterhead.showVitalsStrip}
                onChange={(e) => setLetterhead({ ...letterhead, showVitalsStrip: e.target.checked })} />
              Print the vitals strip
            </label>
            <label className="check">
              <input type="checkbox" checked={letterhead.showAllergyBanner}
                onChange={(e) => setLetterhead({ ...letterhead, showAllergyBanner: e.target.checked })} />
              Print the allergy banner
            </label>
          </div>

          <div className="actions">
            <button type="submit" disabled={busy}>Save letterhead settings</button>
            <button type="button" className="secondary" onClick={() => setCalibrating((v) => !v)}>
              {calibrating ? 'Hide test print' : 'Test print'}
            </button>
            {calibrating && (
              <button type="button" className="secondary" onClick={() => window.print()}>
                Print this sheet
              </button>
            )}
          </div>
          <p className="panel-note" style={{ marginTop: 4 }}>
            The test print puts the zone boxes and a millimetre ruler on one page. Run it on a
            sheet of the clinic&rsquo;s own letterhead to set the margins in one go.
          </p>
        </form>
      )}

      {selected && letterhead && (
        <div className={calibrating ? 'cal-preview' : ''}>
          <CalibrationSheet doctor={selected} letterhead={letterhead} />
        </div>
      )}
    </>
  )
}
